<template><div><code-well-header>
  <dt-progress-circle aria-label="Upload progress" :progress="66" />
</code-well-header>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use a progress circle to communicate deterministic progress to the user — for example, a file upload or media processing operation that reports a percentage complete.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use when you have a concrete progress value (0–100) to display</li>
<li class="d-docsite--list-element">Always provide a meaningful <code v-pre>aria-label</code> describing the operation</li>
<li class="d-docsite--list-element">Show the progress indicator only while an operation is active; remove it once complete</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Don't use for indeterminate loading states — use <a href="/components/loader.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Loader</a> instead</li>
<li class="d-docsite--list-element">Don't omit the <code v-pre>aria-label</code>; it is required for screen reader accessibility</li>
</ul>
</template>
</dialtone-usage>
<h2 id="demo" tabindex="-1" class="d-docsite--header-2">Demo <a class="header-anchor d-link d-docsite--link d-link" href="#demo" aria-hidden="true">#</a></h2>
<code-well-header>
  <dt-stack gap="500" align="center">
    <dt-progress-circle size="800" :progress="demoProgress" :aria-label="`${demoProgress}% complete`" />
    <dt-stack direction="row" gap="400">
      <dt-button size="sm" kind="muted" importance="outlined" :disabled="atMin" @click="setProgress(0)">0%</dt-button>
      <dt-button size="sm" kind="muted" importance="outlined" :disabled="atMin" @click="setProgress(demoProgress - 10)">-10</dt-button>
      <dt-button size="sm" kind="muted" importance="outlined" :disabled="atMax" @click="setProgress(demoProgress + 10)">+10</dt-button>
      <dt-button size="sm" kind="muted" importance="outlined" :disabled="atMax" @click="setProgress(100)">100%</dt-button>
    </dt-stack>
  </dt-stack>
</code-well-header>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="progress" tabindex="-1" class="d-docsite--header-3">Progress <a class="header-anchor d-link d-docsite--link d-link" href="#progress" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-stack direction="row" gap="500">
    <dt-stack v-for="v in [0, 25, 50, 75, 100]" :key="v" gap="200">
      <dt-progress-circle :progress="v" :aria-label="`${v}% complete`" />
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
vueCode='
<dt-progress-circle :progress="{value}" aria-label="{value} complete" />
'
showHtmlWarning />
<h3 id="sizes" tabindex="-1" class="d-docsite--header-3">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The <code v-pre>size</code> prop controls the diameter of the progress circle, aligning to Dialtone icon sizes.</p>
<code-well-header>
  <dt-stack direction="row" gap="500">
    <dt-stack v-for="s in ['100', '200', '300', '400', '500', '600', '700', '800']" :key="s" gap="200">
      <dt-progress-circle :size="s" :progress="66" :aria-label="`size ${s}`" />
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
vueCode='
<dt-progress-circle size="{size}" :progress="66" aria-label="value" />
'
showHtmlWarning />
<h3 id="kinds" tabindex="-1" class="d-docsite--header-3">Kinds <a class="header-anchor d-link d-docsite--link d-link" href="#kinds" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The <code v-pre>kind</code> prop sets the color variant of the progress circle.</p>
<code-well-header>
  <dt-stack direction="row" gap="500">
    <dt-stack v-for="k in ['default', 'brand', 'critical', 'positive', 'warning', 'info', 'ai']" :key="k" gap="200">
      <dt-progress-circle :kind="k" :progress="66" :aria-label="`kind ${k}`" />
    </dt-stack>
  </dt-stack>
</code-well-header>
<code-example-tabs
vueCode='
<dt-progress-circle kind="{kind}" :progress="66" aria-label="value" />
'
showHtmlWarning />
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="progressCircle" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="progress-circle" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">The root element has <code v-pre>role=&quot;progressbar&quot;</code> with <code v-pre>aria-valuemin=&quot;0&quot;</code>, <code v-pre>aria-valuemax=&quot;100&quot;</code>, and <code v-pre>:aria-valuenow</code> bound to the current progress value.</li>
<li class="d-docsite--list-element">Always provide an <code v-pre>aria-label</code> that describes the ongoing operation (e.g., <code v-pre>&quot;Uploading photo&quot;</code>).</li>
<li class="d-docsite--list-element">The element has <code v-pre>tabindex=&quot;-1&quot;</code> so it is not in the natural tab order, but can receive programmatic focus if needed.</li>
</ul>
</div></template>


<script setup>
import { ref, computed } from 'vue';
const demoProgress = ref(25);
const atMin = computed(() => demoProgress.value <= 0);
const atMax = computed(() => demoProgress.value >= 100);
const setProgress = (v) => { demoProgress.value = Math.max(0, Math.min(100, v)); };
</script>